import { categories } from '@/mocks/categories';
import { categoryQuickLinks } from '@/mocks/categories';

export default function FeatureGrid() {
  const featuredMain = categories.slice(0, 3);
  const remainingCats = categories;
  const quickLinks = categoryQuickLinks;

  return (
    <section className="w-full py-20 md:py-28 px-6 md:px-10 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <span className="text-xs font-body text-foreground-500 tracking-widest uppercase">Browse & Discover</span>
          <h2 className="mt-3 text-3xl md:text-5xl font-bold text-foreground-950 font-heading">
            Popular <span className="text-accent-500">Categories</span>
          </h2>
          <p className="mt-3 text-sm text-foreground-600 font-body max-w-md mx-auto">
            Explore our curated product categories with verified finds and detailed quality control information
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-5 mb-6">
          <a
            href={featuredMain[0].url}
            target="_blank"
            rel="noopener nofollow"
            className="relative w-full lg:w-1/2 rounded-2xl overflow-hidden cursor-pointer group"
            style={{ minHeight: '420px' }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Premium%20editorial%20product%20photography%20of%20colorful%20chunky%20sneakers%20in%20sage%20green%20and%20dusty%20rose%20tones%20on%20a%20minimalist%20concrete%20surface%2C%20bold%20geometric%20shadows%20from%20afternoon%20sunlight%20through%20venetian%20blinds%2C%20fashion%20editorial%20aesthetic%2C%20high%20contrast%20lighting%2C%20texture%20details%20on%20suede%20and%20mesh%20materials%2C%20contemporary%20street%20style%2C%20clean%20intentional%20composition%20with%20dramatic%20angular%20shadows%2C%20magazine%20quality%20styling&width=800&height=900&seq=featured-shoes-v2-2025&orientation=portrait"
              alt="Shoes category"
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h3 className="text-3xl md:text-4xl font-bold text-white font-heading">{featuredMain[0].name}</h3>
              <p className="mt-2 text-sm text-white/70 font-body">{featuredMain[0].description}</p>
            </div>
          </a>

          <div className="w-full lg:w-1/2 flex flex-col gap-5">
            <a
              href={featuredMain[1].url}
              target="_blank"
              rel="noopener nofollow"
              className="relative flex-1 rounded-2xl overflow-hidden cursor-pointer group"
              style={{ minHeight: '200px' }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Artistic%20fashion%20flat%20lay%20of%20oversized%20hoodie%20in%20charcoal%20grey%20with%20bright%20neon%20orange%20graphic%20print%2C%20draped%20over%20a%20matte%20black%20surface%2C%20dramatic%20side%20lighting%20from%20single%20studio%20strobe%2C%20moody%20editorial%20aesthetic%20with%20high%20contrast%20shadows%2C%20visible%20fabric%20texture%20and%20print%20detail%2C%20streetwear%20photography%20with%20urban%20edge%2C%20dark%20atmospheric%20background%2C%20fashion%20magazine%20editorial%20style&width=800&height=450&seq=featured-hoodies-v2-2025&orientation=landscape"
                alt="Hoodies/Sweaters category"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white font-heading">{featuredMain[1].name}</h3>
              </div>
            </a>

            <a
              href={featuredMain[2].url}
              target="_blank"
              rel="noopener nofollow"
              className="relative flex-1 rounded-2xl overflow-hidden cursor-pointer group"
              style={{ minHeight: '200px' }}
            >
              <img
                src="https://readdy.ai/api/search-image?query=Minimalist%20product%20photography%20of%20three%20folded%20cotton%20t-shirts%20in%20terracotta%20rust%20olive%20green%20and%20dusty%20blue%20stacked%20neatly%20on%20light%20oak%20wooden%20surface%2C%20soft%20diffused%20morning%20light%20from%20large%20window%2C%20Scandinavian%20aesthetic%2C%20clean%20serene%20composition%20with%20organic%20texture%20details%2C%20visible%20fabric%20weave%20and%20fold%20shadows%2C%20calm%20tranquil%20atmosphere%2C%20high%20end%20basics%20editorial%20styling%2C%20warm%20earthy%20color%20palette&width=800&height=450&seq=featured-tshirts-v2-2025&orientation=landscape"
                alt="T-Shirts category"
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white font-heading">{featuredMain[2].name}</h3>
              </div>
            </a>
          </div>
        </div>

        <div className="flex flex-wrap gap-2.5 justify-center mt-8">
          {remainingCats.map((cat) => (
            <a
              key={cat.name}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium cursor-pointer transition-all duration-200 hover:-translate-y-0.5 whitespace-nowrap ${
                cat.color === 'accent'
                  ? 'bg-accent-100 text-accent-800 hover:bg-accent-200'
                  : 'bg-secondary-100 text-secondary-800 hover:bg-secondary-200'
              }`}
            >
              <span>{cat.name}</span>
              <i className="ri-arrow-right-s-line text-base"></i>
            </a>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 justify-center mt-12 pt-10 border-t border-background-200">
          <span className="text-xs text-foreground-500 font-body self-center mr-2">Quick Links:</span>
          {quickLinks.map((link) => (
            <a
              key={link.name}
              href={link.url}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-full bg-background-50 border border-background-200 text-sm text-foreground-600 hover:text-accent-600 hover:border-accent-200 transition-colors cursor-pointer whitespace-nowrap font-body"
            >
              {link.name}
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}